import {
  sourceSearchResultSchema,
  type AIGeneration,
  type SourceCandidate,
  type SourceSearchResult,
} from '@recipes/contracts';
import { Alert, Badge, Button, Card, CardContent, Input } from '@recipes/ui';
import { useMutation } from '@tanstack/react-query';
import { Download, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useGeneration } from '@/features/ai/use-generation';
import { FormField } from '@/features/shared/form-field';
import { AiConsentNotice } from '@/features/shared/ai-consent-notice';
import { useAiConsent, useFeature } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { api } from '@/lib/api';
import { describeError, describeWorkerError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';

function isSearchResult(value: SourceSearchResult | AIGeneration): value is SourceSearchResult {
  return 'candidates' in value;
}

export function AskSourcesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const aiEnabled = useFeature('ai');
  const searchEnabled = useFeature('source_search');
  const consented = useAiConsent();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<SourceSearchResult | null>(null);
  const [pendingGenerationId, setPendingGenerationId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (q: string) =>
      api.ai.sourceSearch(
        { query: q, language: currentLocale(), maxResults: 5 },
        crypto.randomUUID(),
      ),
    onSuccess: (res) => {
      if (isSearchResult(res)) {
        setResult(res);
        setPendingGenerationId(null);
      } else {
        setResult(null);
        setPendingGenerationId(res.id);
      }
    },
  });

  // The endpoint may answer asynchronously with a generation: follow it until it succeeds.
  const generation = useGeneration(pendingGenerationId);
  const asyncResult =
    generation.generation?.status === 'SUCCEEDED' && !result
      ? sourceSearchResultSchema.safeParse(generation.generation.result)
      : null;
  const effective = result ?? (asyncResult?.success ? asyncResult.data : null);
  const waiting =
    Boolean(pendingGenerationId) && !effective && generation.generation?.status !== 'FAILED';
  const failure =
    mutation.error ??
    (generation.generation?.status === 'FAILED'
      ? new Error(describeWorkerError(generation.generation.errorCode, generation.generation.error))
      : null) ??
    (asyncResult && !asyncResult.success ? new Error(t('ai.unexpectedResult')) : null);

  const canSubmit = aiEnabled && searchEnabled && consented && query.trim().length >= 3;
  const unsourcedHref = `/add/text?text=${encodeURIComponent(query.trim())}`;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('ai.ask.title')}
        description={t('ai.ask.description')}
        backTo="/add"
      />
      {(!aiEnabled || !searchEnabled) && (
        <Alert variant="warning">{t('errors.FEATURE_DISABLED')}</Alert>
      )}
      {aiEnabled && !consented && <AiConsentNotice />}
      <form
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mutation.mutate(query.trim());
        }}
      >
        <FormField label={t('ai.ask.field')} required hint={t('ai.ask.hint')}>
          <Input
            value={query}
            maxLength={500}
            placeholder={t('ai.ask.placeholder')}
            onChange={(e) => setQuery(e.target.value)}
          />
        </FormField>
        <Button type="submit" loading={mutation.isPending || waiting} disabled={!canSubmit}>
          <Search aria-hidden /> {t('ai.ask.submit')}
        </Button>
      </form>

      {waiting && (
        <ProcessingIndicator
          className="mt-6"
          label={t('ai.ask.searching')}
          progress={generation.generation?.status === 'RUNNING' ? generation.progress : null}
        />
      )}
      {failure && (
        <Alert variant="destructive" title={t('ai.failed')} className="mt-6">
          {describeError(failure)}
        </Alert>
      )}

      {effective && (
        <section className="mt-6 space-y-4" aria-label={t('ai.ask.results')}>
          {effective.providerNote && (
            <p className="text-xs text-muted-foreground">{effective.providerNote}</p>
          )}
          {effective.noReliableSource && (
            <Alert variant="warning" title={t('ai.ask.noReliableTitle')}>
              <p>{t('ai.ask.noReliableBody')}</p>
              {effective.unsourcedOfferAvailable && (
                <Button asChild variant="outline" size="sm" className="mt-3">
                  <Link to={unsourcedHref}>{t('ai.ask.generateUnsourced')}</Link>
                </Button>
              )}
            </Alert>
          )}
          {effective.candidates.length > 0 && (
            <ul className="space-y-3">
              {effective.candidates.map((c, i) => (
                <li key={`${c.url}-${i}`}>
                  <CandidateCard
                    candidate={c}
                    onImport={() => navigate(`/add/import?url=${encodeURIComponent(c.url)}`)}
                  />
                </li>
              ))}
            </ul>
          )}
          {!effective.noReliableSource && effective.unsourcedOfferAvailable && (
            <p className="text-sm text-muted-foreground">
              {t('ai.ask.orUnsourced')}{' '}
              <Link to={unsourcedHref} className="text-primary underline">
                {t('ai.ask.generateUnsourced')}
              </Link>
            </p>
          )}
        </section>
      )}
    </PageContainer>
  );
}

function CandidateCard({
  candidate,
  onImport,
}: {
  candidate: SourceCandidate;
  onImport: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold">{candidate.title}</h3>
            <a
              href={candidate.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="break-all text-xs text-primary underline"
            >
              {candidate.domain}
            </a>
          </div>
          <div className="flex flex-wrap gap-1">
            {candidate.hasStructuredRecipe === true && (
              <Badge variant="success">{t('ai.ask.structured')}</Badge>
            )}
            {candidate.hasStructuredRecipe === false && (
              <Badge variant="muted">{t('ai.ask.unstructured')}</Badge>
            )}
            {!candidate.importable && <Badge variant="warning">{t('ai.ask.notImportable')}</Badge>}
          </div>
        </div>
        {candidate.snippet && <p className="text-sm">{candidate.snippet}</p>}
        <p className="text-sm text-muted-foreground">{candidate.explanation}</p>
        {candidate.retrievedFacts.length > 0 && (
          <div className="rounded-md border border-success/40 bg-success/5 p-2 text-sm">
            <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
              <Badge variant="success">{t('ai.ask.retrieved')}</Badge>
            </p>
            <ul className="list-disc space-y-0.5 pl-5">
              {candidate.retrievedFacts.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>
        )}
        {candidate.inference && (
          <div className="rounded-md border border-warning/60 bg-warning/10 p-2 text-sm">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide">
              <Badge variant="warning">{t('ai.ask.inference')}</Badge>
            </p>
            <p>{candidate.inference}</p>
          </div>
        )}
        <Button type="button" size="sm" disabled={!candidate.importable} onClick={onImport}>
          <Download aria-hidden /> {t('ai.ask.importSource')}
        </Button>
      </CardContent>
    </Card>
  );
}
